// Story 10.6 — TanStack Query fetchers for the logged-out public surfaces:
//   GET /api/v1/profiles/by-handle/:handle       (the /u/ collection page)
//   GET /api/v1/public/certificates/:share_id    (the /c/ permalink)
//   GET /api/v1/public/sealed-dossiers/:id        (the /d/ permalink)
//
// All are unauthenticated reads. A 404 (unknown / private / unpublished)
// surfaces as `notFound` so the page can render its empty state instead of
// throwing. A 308 on /d/ is followed transparently by fetch(), landing on
// the certificate JSON — the page handles either shape.

import { useQuery } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";
import type {
  PublicCollectionProfile,
  PublicSealedDossierDetail,
  PublicShareDto,
} from "./publicCollectionTypes";

export interface PublicFetchResult<T> {
  data: T | null;
  notFound: boolean;
}

async function fetchPublicCollection(
  handle: string,
  page: number,
): Promise<PublicFetchResult<PublicCollectionProfile>> {
  const resp = await fetch(
    `/api/v1/profiles/by-handle/${handle}?page=${page}`,
    { method: "GET", credentials: "include" },
  );
  attachRequestIdToScope(resp);
  if (resp.status === 404) return { data: null, notFound: true };
  if (!resp.ok) throw new Error(`Collection fetch failed (HTTP ${resp.status})`);
  const body = (await resp.json()) as { data: PublicCollectionProfile };
  return { data: body.data, notFound: false };
}

async function fetchPublicCertificate(
  shareId: string,
): Promise<PublicFetchResult<PublicShareDto>> {
  const resp = await fetch(`/api/v1/public/certificates/${shareId}`, {
    method: "GET",
    credentials: "include",
  });
  attachRequestIdToScope(resp);
  if (resp.status === 404) return { data: null, notFound: true };
  if (!resp.ok) throw new Error(`Certificate fetch failed (HTTP ${resp.status})`);
  const body = (await resp.json()) as { data: PublicShareDto };
  return { data: body.data, notFound: false };
}

async function fetchPublicSealedDossier(
  dossierId: string,
): Promise<PublicFetchResult<PublicSealedDossierDetail>> {
  const resp = await fetch(`/api/v1/public/sealed-dossiers/${dossierId}`, {
    method: "GET",
    credentials: "include",
  });
  attachRequestIdToScope(resp);
  if (resp.status === 404) return { data: null, notFound: true };
  if (!resp.ok) throw new Error(`Dossier fetch failed (HTTP ${resp.status})`);
  const body = (await resp.json()) as { data: PublicSealedDossierDetail };
  return { data: body.data, notFound: false };
}

export function usePublicCollection(handle: string, page = 1) {
  return useQuery({
    queryKey: ["public-collection", handle, page],
    queryFn: () => fetchPublicCollection(handle, page),
    staleTime: 30_000,
    retry: false,
    enabled: handle.length > 0,
  });
}

export function usePublicCertificate(shareId: string) {
  return useQuery({
    queryKey: ["public-certificate", shareId],
    queryFn: () => fetchPublicCertificate(shareId),
    staleTime: 30_000,
    retry: false,
    enabled: shareId.length > 0,
  });
}

export function usePublicSealedDossier(dossierId: string) {
  return useQuery({
    queryKey: ["public-sealed-dossier", dossierId],
    queryFn: () => fetchPublicSealedDossier(dossierId),
    staleTime: 30_000,
    retry: false,
    enabled: dossierId.length > 0,
  });
}
